import { GS1_PRIMARY_KEY_OPTIONS, type PrimaryKeyAI } from '@myplayer/gs1';
import { IconAlertCircle, IconCheck, IconPlus } from '@myplayer/ui';
import type { FormEvent, ReactElement } from 'react';
import type { WorkspaceOption } from './auth-context.js';
import { Button, Input, ModalDialog, Select } from './components/ui/primitives.js';
import { validateGs1KeyInput } from './products-firestore.js';
import { resolveExampleBrandHostname } from './use-workspace-identifiers.js';

export interface ProductCreateFormProps {
  readonly newAi: PrimaryKeyAI;
  readonly setNewAi: (ai: PrimaryKeyAI) => void;
  readonly newKeyInput: string;
  readonly setNewKeyInput: (val: string) => void;
  readonly newTitle: string;
  readonly setNewTitle: (val: string) => void;
  readonly newBrand: string;
  readonly setNewBrand: (val: string) => void;
  readonly newDefaultUrl: string;
  readonly setNewDefaultUrl: (val: string) => void;
  readonly newWorkspaceId?: string | undefined;
  readonly setNewWorkspaceId?: ((wsId: string) => void) | undefined;
  readonly availableWorkspaces?: readonly WorkspaceOption[] | undefined;
  readonly showWorkspaceSelector?: boolean | undefined;
  readonly newFormError: string | null;
  readonly creatingProduct: boolean;
  readonly effectiveResolverHost?: string;
  readonly onSubmit: (e: FormEvent) => Promise<void>;
  readonly onCancel: () => void;
}

export function ProductCreateForm({
  newAi,
  setNewAi,
  newKeyInput,
  setNewKeyInput,
  newTitle,
  setNewTitle,
  newBrand,
  setNewBrand,
  newDefaultUrl,
  setNewDefaultUrl,
  newWorkspaceId,
  setNewWorkspaceId,
  availableWorkspaces,
  showWorkspaceSelector,
  newFormError,
  creatingProduct,
  effectiveResolverHost,
  onSubmit,
  onCancel,
}: ProductCreateFormProps): ReactElement {
  const selectedKeyOption =
    GS1_PRIMARY_KEY_OPTIONS.find((opt) => opt.code === newAi) ?? GS1_PRIMARY_KEY_OPTIONS[0];
  const hostForPlaceholder = resolveExampleBrandHostname(effectiveResolverHost);
  const trimmedKey = newKeyInput.trim();
  const liveKeyValidation = trimmedKey.length > 0 ? validateGs1KeyInput(newAi, trimmedKey) : null;

  return (
    <ModalDialog
      open={true}
      title="Add product"
      subtitle="Enter your product name, GTIN barcode, and where scanning should go."
      icon={<IconPlus />}
      maxWidthClassName="max-w-2xl"
      submitting={creatingProduct}
      onClose={onCancel}
      data-testid="new-product-form-card"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <div className="space-y-4">
          {showWorkspaceSelector &&
            availableWorkspaces !== undefined &&
            availableWorkspaces.length > 0 &&
            setNewWorkspaceId !== undefined && (
              <div className="space-y-1.5">
                <label
                  htmlFor="new-product-workspace"
                  className="block text-[12px] font-medium text-fg"
                >
                  Target workspace
                </label>
                <Select
                  id="new-product-workspace"
                  data-testid="new-product-workspace-select"
                  value={newWorkspaceId ?? availableWorkspaces[0]?.workspaceId ?? ''}
                  onChange={(e) => setNewWorkspaceId(e.target.value)}
                >
                  {availableWorkspaces.map((ws) => (
                    <option key={ws.workspaceId} value={ws.workspaceId}>
                      {ws.name} ({ws.workspaceId})
                    </option>
                  ))}
                </Select>
              </div>
            )}
          <div className="space-y-1.5">
            <label htmlFor="new-product-title" className="block text-[12px] font-medium text-fg">
              Product name
            </label>
            <Input
              id="new-product-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Organic Oat Milk 1L"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="gs1-key-input" className="block text-[12px] font-medium text-fg">
              GTIN ({selectedKeyOption?.keyName ?? 'barcode number'})
            </label>
            <Input
              id="gs1-key-input"
              value={newKeyInput}
              onChange={(e) => setNewKeyInput(e.target.value)}
              placeholder={selectedKeyOption?.placeholder ?? '09506000134352'}
              className="tabular-nums"
            />
            {liveKeyValidation !== null && (
              <p
                data-testid="live-key-check-indicator"
                className={`flex items-center gap-1.5 text-[11px] font-medium ${
                  liveKeyValidation.ok ? 'text-status-valid' : 'text-fg-secondary'
                }`}
              >
                {liveKeyValidation.ok ? (
                  <>
                    <IconCheck className="h-3.5 w-3.5 shrink-0" />
                    <span>Valid {selectedKeyOption?.keyName ?? 'GTIN'} barcode</span>
                  </>
                ) : (
                  <>
                    <IconAlertCircle className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      Enter a valid {selectedKeyOption?.keyName ?? 'GTIN'} with check digit
                    </span>
                  </>
                )}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="new-product-default-url"
              className="block text-[12px] font-medium text-fg"
            >
              Where should scanning go?
            </label>
            <Input
              id="new-product-default-url"
              value={newDefaultUrl}
              onChange={(e) => setNewDefaultUrl(e.target.value)}
              placeholder={`https://${hostForPlaceholder}/products/oat-milk`}
            />
          </div>

          <div
            data-testid="create-advanced-section"
            className="grid gap-4 border-t border-subtle pt-4 sm:grid-cols-2"
          >
            <div className="space-y-1.5">
              <label
                htmlFor="new-product-brand"
                className="block text-[12px] font-medium text-fg-secondary"
              >
                Brand (optional)
              </label>
              <Input
                id="new-product-brand"
                value={newBrand}
                onChange={(e) => setNewBrand(e.target.value)}
                placeholder="MyPlayer Foods"
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="gs1-ai-select"
                className="block text-[12px] font-medium text-fg-secondary"
              >
                Barcode type
              </label>
              <Select
                id="gs1-ai-select"
                value={newAi}
                onChange={(e) => setNewAi(e.target.value as PrimaryKeyAI)}
              >
                {GS1_PRIMARY_KEY_OPTIONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>

        {newFormError !== null && (
          <p
            role="alert"
            data-testid="new-product-error"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] font-medium text-status-error"
          >
            {newFormError}
          </p>
        )}

        <div className="flex justify-end gap-2 border-t border-subtle pt-4">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onCancel}
            disabled={creatingProduct}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="sm" disabled={creatingProduct}>
            <span>{creatingProduct ? 'Saving...' : 'Save'}</span>
          </Button>
        </div>
      </form>
    </ModalDialog>
  );
}
